/**
 * Utility functions for SavedCallScriptViewer
 */

/**
 * Strips the $default suffix from section names
 * Used for display purposes - sections marked with $default should show clean names
 */
const stripDefaultSuffix = (name) => {
  if (!name || typeof name !== 'string') return name;
  return name.replace(/\$default$/i, '').trim();
};

/**
 * Match submission fields to their original script sections
 *
 * @param {Array} submissionFields - Array of submitted field objects (each has a `field` property with `id`)
 * @param {Array} scriptSections - Array of section objects from the original script (each has `fields` array)
 * @returns {Object} - { matched: { [sectionId]: { ...sectionData, fields: [...matchedFields] } }, unmatched: [...unmatchedFields] }
 */
export const matchSubmissionToScript = (submissionFields, scriptSections) => {
  const matched = {};
  const unmatched = [];

  // Debug logging - remove after fixing
  // eslint-disable-next-line no-console
  console.log('[matchSubmissionToScript] Input:', {
    submissionFieldsCount: submissionFields?.length,
    scriptSectionsCount: scriptSections?.length,
    sectionDetails: scriptSections?.map((s) => ({
      id: s.id,
      name: s.name,
      fieldsCount: s.fields?.length,
      fieldIds: s.fields?.map((f) => f.id),
    })),
    submissionFieldIds: submissionFields?.map((sf) => sf?.field?.id),
  });

  // Build a map of field ID to section for fast lookup
  const fieldToSectionMap = new Map();
  const fieldToOriginalFieldMap = new Map();

  scriptSections?.forEach((section) => {
    section.fields?.forEach((field) => {
      if (field?.id) {
        fieldToSectionMap.set(field.id, section);
        fieldToOriginalFieldMap.set(field.id, field);
      }
    });
  });

  // Process each submission field
  submissionFields?.forEach((submissionField) => {
    const fieldId = submissionField?.field?.id;

    if (!fieldId) {
      // No field ID - can't match
      unmatched.push(submissionField);
      return;
    }

    const section = fieldToSectionMap.get(fieldId);

    if (section) {
      // Found the section for this field
      const sectionId = section.id;

      if (!matched[sectionId]) {
        // Initialize the section in matched results
        matched[sectionId] = {
          id: section.id,
          name: stripDefaultSuffix(section.name),
          description: section.description || '',
          order: section.order ?? 0,
          fields: [],
        };
      }

      // Add the submission field with its original field definition
      matched[sectionId].fields.push({
        ...submissionField,
        originalField: fieldToOriginalFieldMap.get(fieldId),
      });
    } else {
      // Field not found in any section - add to unmatched
      unmatched.push(submissionField);
    }
  });

  // Sort fields within each section by their original order
  Object.values(matched).forEach((section) => {
    section.fields.sort((a, b) => {
      const orderA = a.originalField?.order ?? a.field?.order ?? 0;
      const orderB = b.originalField?.order ?? b.field?.order ?? 0;
      return orderA - orderB;
    });
  });

  // Debug logging for results - remove after fixing
  // eslint-disable-next-line no-console
  console.log('[matchSubmissionToScript] Results:', {
    matchedSections: Object.keys(matched).length,
    matchedDetails: Object.entries(matched).map(([sectionId, section]) => ({
      sectionId,
      sectionName: section.name,
      fieldsCount: section.fields.length,
    })),
    unmatchedCount: unmatched.length,
    unmatchedFieldIds: unmatched.map((uf) => uf?.field?.id),
  });

  return { matched, unmatched };
};

export default { matchSubmissionToScript };
